"use client";

import { useState } from "react";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import type { KundaliRequest } from "@/lib/api/kundali";
import type { ChartRemedyResponse } from "@/lib/api/remedies";
import { getRemediesForChart } from "@/lib/api/remedies";
import Remedies from "@/components/remedies/Remedies";
import Badge from "@ishvaram/ui/Badge";
import Typography from "@ishvaram/ui/Typography";

export default function RemediesClient({
  defaultLanguage = "en",
}: { defaultLanguage?: "en" | "hi" } = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ChartRemedyResponse | null>(null);
  const isHindi = defaultLanguage === "hi";

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const resp = await getRemediesForChart(data);
      setResult(resp);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : isHindi ? "कुछ गड़बड़ हुई" : "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return <Remedies result={result} />;
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <Badge
            label={isHindi ? "व्यक्तिगत वैदिक उपाय" : "Personalized Vedic Remedies"}
            variant="accent"
            className="mb-4"
          />
          <Typography
            variant="headline"
            className="text-[28px] sm:text-[36px] font-bold leading-tight text-ish-dark font-serif"
          >
            {isHindi ? "अपने दोष के उपाय जानें" : "Discover Your Dosha Remedies"}
          </Typography>
          <Typography
            variant="body"
            className="mt-4 text-[15px] leading-relaxed max-w-xl mx-auto text-ish-muted"
          >
            {isHindi
              ? "अपनी जन्म कुंडली में मौजूद दोषों की पहचान करें और ईश्वरम के सिद्ध (अभिमंत्रित) उत्पादों के साथ व्यक्तिगत उपाय बंडल पाएं।"
              : "Enter your birth details to discover which doshas are in your chart and get personalized remedy bundles with Siddh (energized) products from Ishvaram."}
          </Typography>
        </div>

        {/* Birth Form */}
        <div className="max-w-xl mx-auto">
          <BirthDetailsForm
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel={isHindi ? "मेरे उपाय देखें" : "Check My Remedies"}
            error={error}
            language={defaultLanguage}
            analyticsToolName="remedies"
          />
        </div>
      </div>
    </div>
  );
}
